import React, { useEffect, useRef, useState } from "react";
import { api, formatError } from "../../lib/api";
import { formatDate, statusColor } from "../../lib/format";
import { toast } from "sonner";
import { Beer, Wine, Plus, Star, AlertTriangle, Check, ClipboardList, Shield, RotateCcw, Receipt } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";

const ALL_TABS = [
  { key: "bill", label: "Bill Settlement", icon: Receipt, feature: "loyalty_points" },
  { key: "open", label: "Open a Tab", icon: Plus, feature: "bottle_locker" },
  { key: "serve", label: "Serve from Tab", icon: Wine, feature: "bottle_locker" },
  { key: "stamps", label: "Add Stamps", icon: ClipboardList, feature: "stamp_cards" },
  { key: "feedback", label: "Feedback", icon: Star, feature: "feedback" },
];

export function ActionPanel({ customer, detail, onRefresh, venue, externalTab, onExternalTabChange, hideTabBar, isMobile, onClear }) {
  const { hasFeature } = useAuth();
  const TABS = ALL_TABS.filter(t => !t.feature || hasFeature(t.feature));
  const [internalTab, setInternalTab] = useState(() => TABS[0]?.key || "feedback");
  const clearOrRefresh = onClear || onRefresh;

  // Use external tab control when provided (mobile layout lifts this state)
  const tab = externalTab || internalTab;
  const setTab = (key) => {
    if (onExternalTabChange) onExternalTabChange(key);
    else setInternalTab(key);
  };

  // If the active tab becomes invisible (feature toggled), switch to first visible
  useEffect(() => {
    if (TABS.length > 0 && !TABS.find(t => t.key === tab)) {
      setTab(TABS[0].key);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!customer) {
    return (
      <div className="bg-[#1A1A1A] border border-white/10 rounded-xl p-10 text-center">
        <Beer size={36} className="mx-auto text-[#9D00FF] mb-3" />
        <div className="text-lg font-semibold">Select a customer to begin</div>
        <p className="text-sm text-[#A0A0A0] mt-1">
          Pick from search or add a new customer to open a tab, serve pegs, log visits and capture feedback.
        </p>
      </div>
    );
  }

  if (TABS.length === 0) {
    return (
      <div className="bg-[#1A1A1A] border border-white/10 rounded-xl p-10 text-center">
        <AlertTriangle size={32} className="mx-auto text-[#A0A0A0] mb-3" />
        <p className="text-sm text-[#A0A0A0]">No features are enabled. Contact TabOn support.</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {!hideTabBar && TABS.length > 1 && (
        <div className="flex items-center gap-2 overflow-x-auto pb-1">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                data-testid={`action-tab-${t.key}`}
                onClick={() => setTab(t.key)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg font-semibold text-sm whitespace-nowrap transition-all min-h-[44px] ${
                  active
                    ? "bg-[#9D00FF] text-white tb-glow"
                    : "bg-[#1A1A1A] border border-white/10 text-[#A0A0A0] hover:text-white hover:border-white/30"
                }`}
              >
                <Icon size={16} /> {t.label}
              </button>
            );
          })}
        </div>
      )}

      <div className={`bg-[#1A1A1A] border border-white/10 rounded-xl tb-fade-up ${isMobile ? "p-4" : "p-6"}`}>
        {tab === "bill" && <BillSettlementForm customer={customer} venue={venue} onDone={onRefresh} onClear={clearOrRefresh} isMobile={isMobile} detail={detail} />}
        {tab === "open" && <OpenTabForm customer={customer} venue={venue} onDone={onRefresh} onClear={clearOrRefresh} />}
        {tab === "serve" && (
          <ServeFromTab customer={customer} detail={detail} venue={venue} onDone={onRefresh} onClear={clearOrRefresh} />
        )}
        {tab === "stamps" && <AddStampsForm customer={customer} onDone={onRefresh} onClear={clearOrRefresh} />}
        {tab === "feedback" && <FeedbackForm customer={customer} onDone={onRefresh} onClear={clearOrRefresh} />}
      </div>
    </div>
  );
}

// ---------------- OTP Verify Screen ----------------
function OTPVerifyScreen({ pendingId, brand, remainingMl, customerName, phoneMasked, expiresAt,
                           apiBase, onSuccess, onCancel }) {
  const [digits, setDigits] = useState(Array(6).fill(""));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [shake, setShake] = useState(false);
  const [countdown, setCountdown] = useState(() => {
    const diff = Math.floor((new Date(expiresAt) - Date.now()) / 1000);
    return Math.max(0, diff);
  });
  const [resendCooldown, setResendCooldown] = useState(30);
  const [resending, setResending] = useState(false);
  const [showOverride, setShowOverride] = useState(false);
  const inputRefs = useRef([]);

  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  useEffect(() => {
    if (countdown <= 0) return;
    const t = setTimeout(() => setCountdown((c) => Math.max(0, c - 1)), 1000);
    return () => clearTimeout(t);
  }, [countdown]);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown((c) => Math.max(0, c - 1)), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  const formatCountdown = (secs) => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m}:${String(s).padStart(2, "0")}`;
  };

  const submitOTP = async (code) => {
    if (loading) return;
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post(`${apiBase}/verify-otp`, {
        pending_id: pendingId,
        otp_code: code,
      });
      onSuccess(data);
    } catch (err) {
      const msg = formatError(err);
      setError(msg);
      setShake(true);
      setTimeout(() => setShake(false), 600);
      setDigits(Array(6).fill(""));
      setTimeout(() => inputRefs.current[0]?.focus(), 50);
    } finally {
      setLoading(false);
    }
  };

  const handleDigitChange = (idx, val) => {
    const d = val.replace(/\D/g, "").slice(-1);
    const next = [...digits];
    next[idx] = d;
    setDigits(next);
    if (d && idx < 5) {
      inputRefs.current[idx + 1]?.focus();
    }
    if (next.every((x) => x !== "") && d) {
      submitOTP(next.join(""));
    }
  };

  const handleKeyDown = (idx, e) => {
    if (e.key === "Backspace" && !digits[idx] && idx > 0) {
      const next = [...digits];
      next[idx - 1] = "";
      setDigits(next);
      inputRefs.current[idx - 1]?.focus();
    }
    if (e.key === "Enter") {
      const code = digits.join("");
      if (code.length === 6) submitOTP(code);
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0 || resending) return;
    setResending(true);
    try {
      const { data } = await api.post(`${apiBase}/resend-otp`, { pending_id: pendingId });
      const newExpiry = new Date(data.expires_at);
      setCountdown(Math.floor((newExpiry - Date.now()) / 1000));
      setResendCooldown(30);
      setDigits(Array(6).fill(""));
      setError("");
      setTimeout(() => inputRefs.current[0]?.focus(), 50);
      toast.success("New OTP sent to customer's WhatsApp");
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="flex flex-col gap-5">
      {/* Header */}
      <div className="text-center">
        <div className="text-3xl mb-2">🔐</div>
        <h3 className="text-lg font-bold">OTP Sent</h3>
        <p className="text-sm text-[#A0A0A0] mt-1">
          Verification code sent to customer's WhatsApp<br />
          <span className="text-white font-medium">{phoneMasked}</span>
        </p>
      </div>

      {/* Tab info */}
      <div className="bg-[#2D1B4E]/60 border border-[#9D00FF]/40 rounded-lg p-4 text-center">
        <div className="text-[#9D00FF] font-bold text-sm">{brand}</div>
        <div className="text-2xl font-black text-white mt-1">{remainingMl}<span className="text-sm text-[#A0A0A0] ml-1">ml remaining</span></div>
        <div className="text-xs text-[#A0A0A0] mt-1">for {customerName}</div>
      </div>

      {/* 6-digit OTP input */}
      <div className={`flex justify-center gap-2 ${shake ? "animate-[shake_0.4s_ease-in-out]" : ""}`}
           style={shake ? { animation: "shake 0.4s ease-in-out" } : {}}>
        {digits.map((d, i) => (
          <input
            key={i}
            ref={(el) => (inputRefs.current[i] = el)}
            type="text"
            inputMode="numeric"
            maxLength={1}
            value={d}
            onChange={(e) => handleDigitChange(i, e.target.value)}
            onKeyDown={(e) => handleKeyDown(i, e)}
            disabled={loading || countdown <= 0}
            className={`w-12 h-14 text-center text-2xl font-bold rounded-xl border-2 bg-[#0A0A0A] text-white transition-all outline-none
              ${d ? "border-[#9D00FF] bg-[#2D1B4E]/40" : "border-white/20"}
              ${shake ? "border-red-500" : ""}
              focus:border-[#9D00FF] focus:ring-1 focus:ring-[#9D00FF]/40
              disabled:opacity-50`}
          />
        ))}
      </div>

      {error && (
        <div className="text-center text-red-400 text-sm font-medium">{error}</div>
      )}

      {loading && (
        <div className="text-center text-[#9D00FF] text-sm">Verifying…</div>
      )}

      {/* Countdown */}
      <div className="text-center">
        {countdown > 0 ? (
          <span className="text-sm text-[#A0A0A0]">
            Expires in <span className="text-white font-semibold">{formatCountdown(countdown)}</span>
          </span>
        ) : (
          <span className="text-sm text-red-400 font-medium">OTP expired</span>
        )}
      </div>

      {/* Resend */}
      <button
        type="button"
        onClick={handleResend}
        disabled={resendCooldown > 0 || resending}
        className="w-full flex items-center justify-center gap-2 py-3 min-h-[44px] rounded-lg border transition font-semibold text-sm
          disabled:opacity-40 disabled:cursor-not-allowed
          enabled:bg-[#0A0A0A] enabled:border-white/20 enabled:text-white enabled:hover:border-[#9D00FF]/60"
      >
        <RotateCcw size={14} />
        {resending ? "Sending…" : resendCooldown > 0 ? `Resend OTP (${resendCooldown}s)` : "Resend OTP"}
      </button>

      {/* Manager override link */}
      <div className="text-center">
        <button
          type="button"
          onClick={() => setShowOverride(true)}
          className="text-xs text-[#A0A0A0] hover:text-white underline underline-offset-2 transition"
        >
          Customer can't receive OTP?
        </button>
      </div>

      {/* Cancel */}
      <button
        type="button"
        onClick={onCancel}
        className="text-center text-xs text-[#A0A0A0] hover:text-white transition"
      >
        ← Back to form
      </button>

      {showOverride && (
        <ManagerOverrideModal
          pendingId={pendingId}
          apiBase={apiBase}
          onSuccess={onSuccess}
          onClose={() => setShowOverride(false)}
        />
      )}

      <style>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          20% { transform: translateX(-8px); }
          40% { transform: translateX(8px); }
          60% { transform: translateX(-5px); }
          80% { transform: translateX(5px); }
        }
      `}</style>
    </div>
  );
}

// ---------------- Success Screen ----------------
function OTPSuccessScreen({ brand, remainingMl, customerName, onDone }) {
  useEffect(() => {
    const t = setTimeout(onDone, 3000);
    return () => clearTimeout(t);
  }, [onDone]);

  return (
    <div className="flex flex-col items-center justify-center gap-4 py-10 text-center">
      <div className="w-16 h-16 rounded-full bg-emerald-500/20 border border-emerald-500/40 flex items-center justify-center">
        <Check size={28} className="text-emerald-400" />
      </div>
      <div>
        <div className="text-lg font-bold text-white">Confirmed!</div>
        <p className="text-sm text-[#A0A0A0] mt-1">
          {brand} ({remainingMl}ml) saved for {customerName}
        </p>
      </div>
    </div>
  );
}

// ---------------- Manager Override Modal ----------------
function ManagerOverrideModal({ pendingId, apiBase, onSuccess, onClose }) {
  const [pin, setPin] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (pin.length !== 4) { setError("PIN must be 4 digits"); return; }
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post(`${apiBase}/manager-override`, {
        pending_id: pendingId,
        manager_pin: pin,
      });
      onSuccess(data);
    } catch (err) {
      setError(formatError(err));
      setPin("");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-4">
      <div className="bg-[#1A1A1A] border border-[#9D00FF]/40 rounded-xl w-full max-w-sm p-6 tb-fade-up">
        <div className="flex items-center gap-2 text-[#9D00FF] mb-2">
          <Shield size={16} />
          <span className="font-semibold uppercase text-xs tracking-wider">Admin Override Required</span>
        </div>
        <p className="text-sm text-[#A0A0A0] mb-5">
          Enter the admin override PIN to skip OTP verification. This action is logged.
        </p>
        <form onSubmit={submit} className="flex flex-col gap-4">
          <div className="flex justify-center gap-3">
            {[0, 1, 2, 3].map((i) => (
              <div
                key={i}
                className={`w-12 h-14 rounded-xl border-2 flex items-center justify-center text-xl font-bold
                  ${pin.length > i ? "border-[#9D00FF] bg-[#2D1B4E]/40 text-white" : "border-white/20 bg-[#0A0A0A] text-transparent"}`}
              >
                {pin.length > i ? "•" : "○"}
              </div>
            ))}
          </div>
          <input
            type="password"
            inputMode="numeric"
            maxLength={4}
            value={pin}
            onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 4))}
            className="sr-only"
            autoFocus
          />
          <p className="text-xs text-[#A0A0A0] text-center -mt-2">Enter the 4-digit admin override PIN</p>
          {error && <div className="text-red-400 text-sm text-center">{error}</div>}
          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              onClick={onClose}
              className="bg-[#0A0A0A] border border-white/10 rounded-lg py-3 min-h-[44px] font-semibold text-sm"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || pin.length !== 4}
              className="bg-[#9D00FF] disabled:opacity-50 rounded-lg py-3 min-h-[44px] font-semibold text-sm flex items-center justify-center gap-2"
            >
              {loading ? "…" : <><Shield size={14} /> Confirm Override</>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ---------------- Open Tab ----------------
function OpenTabForm({ customer, venue, onDone, onClear }) {
  const [brands, setBrands] = useState([]);
  const [brand, setBrand] = useState("");
  const [brandQuery, setBrandQuery] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [newBrand, setNewBrand] = useState("");
  const [size, setSize] = useState(750);
  const [consumed, setConsumed] = useState(0);
  const [expiryDays, setExpiryDays] = useState(venue?.default_expiry_days || 60);
  const [loading, setLoading] = useState(false);
  const bnCapture = venue?.bill_number_capture || "off";
  const [billNumber, setBillNumber] = useState("");

  // OTP flow state
  const [phase, setPhase] = useState("form"); // "form" | "otp" | "done"
  const [pendingData, setPendingData] = useState(null);

  useEffect(() => {
    api.get("/brands").then(({ data }) => setBrands(data.brands || []));
  }, []);

  const remaining = Math.max(0, size - (Number(consumed) || 0));
  const expiryDate = new Date(Date.now() + expiryDays * 24 * 60 * 60 * 1000);

  const filtered = brandQuery
    ? brands.filter((b) => b.toLowerCase().includes(brandQuery.toLowerCase())).slice(0, 8)
    : brands.slice(0, 8);

  const saveCustomBrand = async () => {
    if (!newBrand.trim()) return;
    try {
      await api.post("/brands", { name: newBrand.trim() });
      const { data } = await api.get("/brands");
      setBrands(data.brands);
      setBrand(newBrand.trim());
      setBrandQuery(newBrand.trim());
      setNewBrand("");
      setShowAdd(false);
      toast.success("Brand added");
    } catch (err) {
      toast.error(formatError(err));
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!brand) { toast.error("Pick a brand"); return; }
    if (consumed > size) { toast.error("Consumed can't exceed bottle size"); return; }
    if (bnCapture === "required" && !billNumber.trim()) {
      toast.error("Bill number is required"); return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/staff/tabs/initiate", {
        customer_id: customer.id,
        brand,
        bottle_size_ml: Number(size),
        consumed_today_ml: Number(consumed) || 0,
        expiry_days: Number(expiryDays),
        bill_number: bnCapture !== "off" ? billNumber.trim() || null : null,
      });
      setPendingData(data);
      setPhase("otp");
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setLoading(false);
    }
  };

  if (phase === "otp" && pendingData) {
    return (
      <OTPVerifyScreen
        pendingId={pendingData.pending_id}
        brand={pendingData.brand}
        remainingMl={pendingData.remaining_ml}
        customerName={pendingData.customer_name}
        phoneMasked={pendingData.customer_phone_masked}
        expiresAt={pendingData.expires_at}
        apiBase="/staff/tabs"
        onSuccess={() => {
          setPhase("done");
        }}
        onCancel={() => {
          setPhase("form");
          setPendingData(null);
        }}
      />
    );
  }

  if (phase === "done" && pendingData) {
    return (
      <OTPSuccessScreen
        brand={pendingData.brand}
        remainingMl={pendingData.remaining_ml}
        customerName={pendingData.customer_name}
        onDone={onClear}
      />
    );
  }

  return (
    <form onSubmit={submit} className="flex flex-col gap-5">
      <div>
        <h3 className="text-lg font-bold mb-0.5">Open a new bottle tab</h3>
        <p className="text-xs text-[#A0A0A0]">Pick a brand, set bottle size, enter tonight's consumption.</p>
      </div>

      <div>
        <label className="text-xs font-bold uppercase tracking-wider text-[#A0A0A0] mb-1.5 block">Brand</label>
        <div className="relative">
          <input
            data-testid="brand-search-input"
            value={brandQuery}
            onChange={(e) => {
              setBrandQuery(e.target.value);
              setBrand("");
            }}
            placeholder="Search or pick a brand…"
            className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg px-4 py-3 min-h-[44px] text-white focus:border-[#9D00FF] focus:ring-1 focus:ring-[#9D00FF]/40"
          />
          {brandQuery && !brand && (
            <div className="absolute z-10 left-0 right-0 top-full mt-1 bg-[#1A1A1A] border border-white/10 rounded-lg max-h-56 overflow-y-auto shadow-xl">
              {filtered.map((b) => (
                <button
                  type="button"
                  key={b}
                  data-testid={`brand-option-${b.replace(/\s+/g, "-").toLowerCase()}`}
                  onClick={() => {
                    setBrand(b);
                    setBrandQuery(b);
                  }}
                  className="w-full text-left px-4 py-2.5 hover:bg-[#9D00FF]/10 text-white text-sm border-b border-white/5 last:border-0"
                >
                  {b}
                </button>
              ))}
              <button
                type="button"
                onClick={() => setShowAdd(true)}
                data-testid="add-custom-brand-button"
                className="w-full text-left px-4 py-2.5 text-[#9D00FF] font-semibold text-sm hover:bg-[#9D00FF]/10"
              >
                + Add new brand
              </button>
            </div>
          )}
        </div>
        {brand && (
          <div className="mt-2 text-xs text-[#9D00FF]">
            Selected: <span className="text-white font-semibold">{brand}</span>
          </div>
        )}
      </div>

      {showAdd && (
        <div className="bg-[#0A0A0A] border border-[#9D00FF]/30 rounded-lg p-3 flex gap-2">
          <input
            data-testid="custom-brand-input"
            value={newBrand}
            onChange={(e) => setNewBrand(e.target.value)}
            placeholder="Custom brand name"
            className="flex-1 bg-transparent text-white outline-none px-2"
          />
          <button type="button" onClick={saveCustomBrand} className="bg-[#9D00FF] px-3 py-1.5 rounded text-sm font-semibold">
            Save
          </button>
          <button type="button" onClick={() => setShowAdd(false)} className="text-[#A0A0A0] px-2 text-sm">
            Cancel
          </button>
        </div>
      )}

      <div>
        <label className="text-xs font-bold uppercase tracking-wider text-[#A0A0A0] mb-2 block">Bottle Size</label>
        <div className="grid grid-cols-3 gap-2">
          {[
            { v: 750, label: "750 ml" },
            { v: 1000, label: "1 L" },
            { v: 1500, label: "1.5 L" },
          ].map((opt) => (
            <button
              type="button"
              key={opt.v}
              data-testid={`bottle-size-${opt.v}`}
              onClick={() => setSize(opt.v)}
              className={`py-3 min-h-[44px] rounded-lg font-semibold text-sm border transition ${
                size === opt.v
                  ? "bg-[#9D00FF] border-[#9D00FF] text-white"
                  : "bg-[#0A0A0A] border-white/10 text-[#A0A0A0] hover:text-white"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className="text-xs font-bold uppercase tracking-wider text-[#A0A0A0] mb-1.5 block">
            Consumed tonight (ml)
          </label>
          <input
            data-testid="consumed-input"
            type="number"
            min="0"
            max={size}
            value={consumed}
            onChange={(e) => setConsumed(e.target.value)}
            onWheel={(e) => e.target.blur()}
            className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg px-4 py-3 min-h-[44px] text-white focus:border-[#9D00FF] focus:ring-1 focus:ring-[#9D00FF]/40"
          />
        </div>
        <div>
          <label className="text-xs font-bold uppercase tracking-wider text-[#A0A0A0] mb-1.5 block">
            Remaining
          </label>
          <div
            data-testid="remaining-live"
            className="bg-[#2D1B4E] border border-[#9D00FF]/40 rounded-lg px-4 py-3 min-h-[44px] text-white font-bold text-xl flex items-center"
          >
            {remaining} <span className="text-xs text-[#A0A0A0] ml-2">ml remaining</span>
          </div>
        </div>
      </div>

      <div>
        <label className="text-xs font-bold uppercase tracking-wider text-[#A0A0A0] mb-2 block">Expiry</label>
        <div className="grid grid-cols-3 gap-2">
          {[30, 60, 90].map((d) => (
            <button
              type="button"
              key={d}
              data-testid={`expiry-${d}`}
              onClick={() => setExpiryDays(d)}
              className={`py-3 min-h-[44px] rounded-lg font-semibold text-sm border transition ${
                expiryDays === d
                  ? "bg-[#9D00FF] border-[#9D00FF] text-white"
                  : "bg-[#0A0A0A] border-white/10 text-[#A0A0A0] hover:text-white"
              }`}
            >
              {d} days
            </button>
          ))}
        </div>
        <p className="text-xs text-[#A0A0A0] mt-2">
          Tab valid till <span className="text-white font-semibold">{formatDate(expiryDate)}</span>
        </p>
      </div>

      {bnCapture !== "off" && (
        <div>
          <label className="text-xs font-bold uppercase tracking-wider text-[#A0A0A0] mb-1.5 block">
            BILL / RECEIPT NO.{bnCapture === "required" ? " *" : " (OPTIONAL)"}
          </label>
          <input
            type="text"
            data-testid="tab-bill-number"
            value={billNumber}
            onChange={(e) => setBillNumber(e.target.value)}
            className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg px-4 py-2.5 text-white focus:border-[#9D00FF] focus:ring-1 focus:ring-[#9D00FF]/40"
            placeholder="e.g. INV-1042"
          />
        </div>
      )}

      <div className="bg-[#0A0A0A] border border-white/5 rounded-lg px-4 py-3 text-xs text-[#A0A0A0] flex items-center gap-2">
        <span>📱</span>
        OTP will be sent to customer's WhatsApp ending in {brand && remaining > 0 ? `— ${brand}, ${remaining}ml remaining` : ""}
      </div>

      <button
        type="submit"
        disabled={loading || !brand}
        data-testid="open-tab-submit"
        className="tb-glow bg-[#9D00FF] hover:bg-[#8C00E6] disabled:opacity-50 text-white font-semibold rounded-lg py-3.5 min-h-[48px] transition-all flex items-center justify-center gap-2"
      >
        {loading ? "Sending OTP…" : `Send OTP to Customer`}
      </button>
    </form>
  );
}

// ---------------- Serve From Tab ----------------
function ServeFromTab({ customer, detail, venue, onDone, onClear }) {
  const { hasFeature } = useAuth();
  const bnCapture = venue?.bill_number_capture || "off";
  const showBillNumber = !hasFeature("loyalty_points") && bnCapture !== "off";
  const [selected, setSelected] = useState(null);
  const [mode, setMode] = useState("peg"); // "peg" | "custom"
  const [peg, setPeg] = useState((venue?.default_peg_sizes || [30, 60])[0] || 30);
  const [pegs, setPegs] = useState(1);
  const [customMl, setCustomMl] = useState(180);
  const [billNumber, setBillNumber] = useState("");
  const [loading, setLoading] = useState(false);

  // OTP flow state
  const [phase, setPhase] = useState("form"); // "form" | "otp" | "done"
  const [pendingData, setPendingData] = useState(null);

  const tabs = (detail?.active_tabs || []).filter((t) => t.remaining_ml > 0 && t.status !== "Expired");
  const total = mode === "peg" ? peg * pegs : Math.max(0, Number(customMl) || 0);
  const afterDeduct = selected ? selected.remaining_ml - total : 0;

  const initiateServe = async () => {
    if (!selected || total <= 0) return;
    if (showBillNumber && bnCapture === "required" && !billNumber.trim()) {
      toast.error("Bill number is required"); return;
    }
    setLoading(true);
    try {
      const payload =
        mode === "peg"
          ? { tab_id: selected.id, peg_size_ml: peg, num_pegs: pegs }
          : { tab_id: selected.id, custom_ml: total };
      if (showBillNumber) payload.bill_number = billNumber.trim() || null;
      const { data } = await api.post("/staff/deductions/initiate", payload);
      setPendingData(data);
      setPhase("otp");
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setLoading(false);
    }
  };

  if (phase === "otp" && pendingData) {
    return (
      <OTPVerifyScreen
        pendingId={pendingData.pending_id}
        brand={pendingData.brand}
        remainingMl={pendingData.remaining_ml}
        customerName={pendingData.customer_name}
        phoneMasked={pendingData.customer_phone_masked}
        expiresAt={pendingData.expires_at}
        apiBase="/staff/deductions"
        onSuccess={() => {
          setPhase("done");
        }}
        onCancel={() => {
          setPhase("form");
          setPendingData(null);
        }}
      />
    );
  }

  if (phase === "done" && pendingData) {
    return (
      <OTPSuccessScreen
        brand={pendingData.brand}
        remainingMl={pendingData.remaining_ml}
        customerName={pendingData.customer_name}
        onDone={onClear}
      />
    );
  }

  if (tabs.length === 0) {
    return (
      <div className="text-center py-10">
        <Wine size={32} className="mx-auto text-[#9D00FF] mb-2" />
        <div className="font-semibold">No active tabs</div>
        <p className="text-sm text-[#A0A0A0] mt-1">Open a tab for {customer.name} first.</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      <div>
        <h3 className="text-lg font-bold mb-3">Choose a tab to serve from</h3>
        <div className="grid sm:grid-cols-2 gap-3">
          {tabs.map((t) => (
            <button
              key={t.id}
              type="button"
              data-testid={`serve-tab-${t.id}`}
              onClick={() => setSelected(t)}
              className={`text-left p-4 rounded-lg border transition ${
                selected?.id === t.id
                  ? "bg-[#9D00FF]/15 border-[#9D00FF]"
                  : "bg-[#0A0A0A] border-white/10 hover:border-[#9D00FF]/40"
              }`}
            >
              <div className="flex items-center justify-between mb-1">
                <div className="font-bold text-white">{t.brand}</div>
                <span className={`text-[10px] px-2 py-0.5 rounded-md border uppercase tracking-wider font-bold ${statusColor(t.status)}`}>
                  {t.status}
                </span>
              </div>
              <div className="text-2xl font-black text-[#9D00FF]">{t.remaining_ml}<span className="text-sm text-[#A0A0A0] ml-1">ml</span></div>
              <div className="text-xs text-[#A0A0A0] mt-1">Expires {formatDate(t.expiry_at)}</div>
            </button>
          ))}
        </div>
      </div>

      {selected && (
        <>
          {/* Mode toggle: peg-based OR custom ml */}
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-[#A0A0A0] mb-2 block">
              How much served?
            </label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                data-testid="serve-mode-peg"
                onClick={() => setMode("peg")}
                className={`py-3 min-h-[44px] rounded-lg font-semibold text-sm border transition ${
                  mode === "peg"
                    ? "bg-[#9D00FF] border-[#9D00FF] text-white"
                    : "bg-[#0A0A0A] border-white/10 text-[#A0A0A0] hover:text-white"
                }`}
              >
                Pegs (30/60ml)
              </button>
              <button
                type="button"
                data-testid="serve-mode-custom"
                onClick={() => setMode("custom")}
                className={`py-3 min-h-[44px] rounded-lg font-semibold text-sm border transition ${
                  mode === "custom"
                    ? "bg-[#9D00FF] border-[#9D00FF] text-white"
                    : "bg-[#0A0A0A] border-white/10 text-[#A0A0A0] hover:text-white"
                }`}
              >
                Custom (any ml)
              </button>
            </div>
          </div>

          {mode === "peg" ? (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-[#A0A0A0] mb-2 block">Peg Size</label>
                <div className="grid grid-cols-2 gap-2">
                  {(venue?.default_peg_sizes || [30, 60]).map((p) => (
                    <button
                      key={p}
                      type="button"
                      data-testid={`peg-size-${p}`}
                      onClick={() => setPeg(p)}
                      className={`py-3 min-h-[44px] rounded-lg font-semibold text-sm border transition ${
                        peg === p
                          ? "bg-[#9D00FF] border-[#9D00FF] text-white"
                          : "bg-[#0A0A0A] border-white/10 text-[#A0A0A0] hover:text-white"
                      }`}
                    >
                      {p}ml
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-[#A0A0A0] mb-2 block">No. of pegs</label>
                <div className="flex items-center bg-[#0A0A0A] border border-white/10 rounded-lg overflow-hidden">
                  <button
                    type="button"
                    data-testid="pegs-decrement"
                    onClick={() => setPegs(Math.max(1, pegs - 1))}
                    className="px-4 py-3 min-h-[44px] text-xl font-bold text-[#9D00FF] hover:bg-white/5"
                  >
                    −
                  </button>
                  <div data-testid="pegs-value" className="flex-1 text-center text-xl font-bold text-white">{pegs}</div>
                  <button
                    type="button"
                    data-testid="pegs-increment"
                    onClick={() => setPegs(pegs + 1)}
                    className="px-4 py-3 min-h-[44px] text-xl font-bold text-[#9D00FF] hover:bg-white/5"
                  >
                    +
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-[#A0A0A0] mb-2 block">Amount served (ml)</label>
              <div className="flex gap-2 items-center">
                <input
                  data-testid="custom-ml-input"
                  type="number"
                  min="1"
                  max={selected.remaining_ml}
                  value={customMl}
                  onChange={(e) => setCustomMl(e.target.value.replace(/\D/g, ""))}
                  onWheel={(e) => e.target.blur()}
                  className="flex-1 bg-[#0A0A0A] border border-white/10 rounded-lg px-4 py-3 min-h-[52px] text-white text-center text-3xl font-bold focus:border-[#9D00FF] focus:ring-1 focus:ring-[#9D00FF]/40"
                  placeholder="e.g. 180"
                />
                <span className="text-[#A0A0A0] font-semibold">ml</span>
              </div>
              <div className="flex flex-wrap gap-2 mt-2">
                {[90, 120, 150, 180, 240, 300].map((q) => (
                  <button
                    key={q}
                    type="button"
                    data-testid={`custom-ml-quick-${q}`}
                    onClick={() => setCustomMl(q)}
                    className="px-3 py-1.5 rounded-md text-xs font-semibold bg-[#2D1B4E]/60 border border-[#9D00FF]/30 hover:border-[#9D00FF] text-white transition"
                  >
                    {q}ml
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="bg-[#2D1B4E]/60 border border-[#9D00FF]/40 rounded-lg p-4">
            <div className="text-xs uppercase tracking-wider text-[#A0A0A0] mb-1">Preview</div>
            <div className="text-white">
              Deducting <span className="text-[#9D00FF] font-bold">{total}ml</span> from {selected.brand}.{" "}
              Remaining after: <span className="font-bold">{afterDeduct}ml</span>
            </div>
          </div>

          {showBillNumber && (
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-[#A0A0A0] mb-1.5 block">
                BILL / RECEIPT NO.{bnCapture === "required" ? " *" : " (OPTIONAL)"}
              </label>
              <input
                type="text"
                data-testid="serve-bill-number"
                value={billNumber}
                onChange={(e) => setBillNumber(e.target.value)}
                className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg px-4 py-2.5 text-white focus:border-[#9D00FF] focus:ring-1 focus:ring-[#9D00FF]/40"
                placeholder="e.g. INV-1042"
              />
            </div>
          )}

          <button
            type="button"
            disabled={loading || total <= 0 || total > selected.remaining_ml}
            data-testid="serve-confirm-button"
            onClick={initiateServe}
            className="tb-glow bg-[#9D00FF] hover:bg-[#8C00E6] disabled:opacity-50 text-white font-semibold rounded-lg py-3.5 min-h-[48px] transition-all"
          >
            {loading ? "Sending OTP…"
              : total <= 0 ? "Enter an amount"
              : total > selected.remaining_ml ? "Not enough remaining"
              : `Send OTP to Customer`}
          </button>
        </>
      )}
    </div>
  );
}

// ---------------- Feedback ----------------
function FeedbackForm({ customer, onDone, onClear }) {
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (rating < 1) {
      toast.error("Pick a rating");
      return;
    }
    setLoading(true);
    try {
      await api.post("/feedback", {
        customer_id: customer.id,
        rating,
        comment: comment.slice(0, 200),
      });
      toast.success("Feedback saved");
      setRating(0);
      setComment("");
      onClear();
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="flex flex-col gap-4">
      <div>
        <h3 className="text-lg font-bold mb-0.5">Post-visit feedback</h3>
        <p className="text-xs text-[#A0A0A0]">Capture the vibe before the customer leaves.</p>
      </div>

      <div className="flex items-center justify-center gap-2 py-4">
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            type="button"
            data-testid={`rating-star-${n}`}
            onClick={() => setRating(n)}
            className="transition-transform hover:scale-110"
          >
            <Star
              size={38}
              className={n <= rating ? "fill-[#9D00FF] text-[#9D00FF]" : "text-[#A0A0A0]"}
            />
          </button>
        ))}
      </div>

      <div>
        <label className="text-xs font-bold uppercase tracking-wider text-[#A0A0A0] mb-1.5 block">
          Comment <span className="text-[#A0A0A0] normal-case">({comment.length}/200)</span>
        </label>
        <textarea
          data-testid="feedback-comment"
          value={comment}
          onChange={(e) => setComment(e.target.value.slice(0, 200))}
          rows={3}
          className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg px-4 py-3 text-white focus:border-[#9D00FF] focus:ring-1 focus:ring-[#9D00FF]/40"
          placeholder="Optional — anything the customer mentioned"
        />
      </div>

      <button
        type="submit"
        disabled={loading || rating < 1}
        data-testid="feedback-submit"
        className="tb-glow bg-[#9D00FF] hover:bg-[#8C00E6] disabled:opacity-50 text-white font-semibold rounded-lg py-3.5 min-h-[48px] transition-all"
      >
        {loading ? "Saving…" : "Save Feedback"}
      </button>
    </form>
  );
}


// ---------------- Bill Settlement ----------------
function BillSettlementForm({ customer, venue, onDone, onClear, isMobile, detail }) {
  const { hasFeature } = useAuth();
  const loyaltyEnabled = hasFeature("loyalty_points");
  const bottleLockerEnabled = hasFeature("bottle_locker");

  const [billAmount, setBillAmount] = useState("");
  const [billNumber, setBillNumber] = useState("");
  const [pointsToRedeem, setPointsToRedeem] = useState(0);
  const [limits, setLimits] = useState(null); // server-computed redemption limits (source of truth)
  const [loading, setLoading] = useState(false);
  const [phase, setPhase] = useState("form"); // "form" | "otp" | "done"
  const [pendingData, setPendingData] = useState(null);
  const [result, setResult] = useState(null);
  const [programs, setPrograms] = useState([]);
  const [stampAdditions, setStampAdditions] = useState({});

  // Bottle save state
  const [saveBottle, setSaveBottle] = useState(false);
  const [bottleBrands, setBottleBrands] = useState([]);
  const [bottleBrand, setBottleBrand] = useState("");
  const [bottleBrandQuery, setBrandQuery] = useState("");
  const [bottleSize, setBottleSize] = useState(750);
  const [bottleConsumed, setBottleConsumed] = useState(0);
  const [bottleExpiry, setBottleExpiry] = useState(venue?.default_expiry_days || 60);
  const [showAddBrand, setShowAddBrand] = useState(false);
  const [newBrandName, setNewBrandName] = useState("");

  // Serve from existing tab state
  const [serveFromTab, setServeFromTab] = useState(false);
  const [serveTabId, setServeTabId] = useState("");
  const [servePegSize, setServePegSize] = useState(60);
  const [serveNumPegs, setServeNumPegs] = useState(1);
  const [serveCustomMl, setServeCustomMl] = useState("");
  const [serveUseCustom, setServeUseCustom] = useState(false);

  useEffect(() => {
    if (!hasFeature("stamp_cards")) return;
    api.get("/stamp-programs")
      .then(({ data }) => setPrograms((data || []).filter(p => p.active)))
      .catch(() => setPrograms([]));
  }, []);

  useEffect(() => {
    if (!bottleLockerEnabled) return;
    api.get("/brands").then(({ data }) =>
      setBottleBrands(data.brands || [])
    ).catch(() => {});
  }, [bottleLockerEnabled]);

  // Reset stamp additions and bottle state when the customer changes
  useEffect(() => {
    setStampAdditions({});
    setLimits(null); // drop the previous customer's limits until the fetch below refills
    setSaveBottle(false);
    setBottleBrand("");
    setBrandQuery("");
    setBottleConsumed(0);
    setServeFromTab(false);
    setServeTabId("");
    setServeNumPegs(1);
    setServeCustomMl("");
  }, [customer?.id]);

  const ptsBalance = Number(customer?.points_balance || 0);
  const bill = Number(billAmount) || 0;
  const rate = Number(venue?.loyalty_rate_per_100 || 10);   // one rate for every channel (O-3)
  // Points the customer may redeem on THIS bill. The server is the source of
  // truth (it applies the %-cap, minimum-bill and same-day-cooldown rules and,
  // for cooldown, an aggregation the browser cannot do). Until the first fetch
  // returns, fall back to the local balance∧bill ceiling so defaults behave
  // exactly as before; always also clamp to the current bill in case `limits`
  // is momentarily stale from a previous keystroke.
  const redeemable = Math.max(0, Math.min(
    limits ? Number(limits.redeemable) : ptsBalance,
    Math.floor(bill)
  ));
  const netPayable = Math.max(0, bill - pointsToRedeem);
  const willEarn = bill > 0 ? Math.floor((bill - pointsToRedeem) * rate / 100) : 0;
  const bnCapture = venue?.bill_number_capture || "off"; // "off" | "optional" | "required"; default off = safe

  const activeTabs = detail?.active_tabs?.filter(t => t.remaining_ml > 0) || [];
  const selectedServeTab = activeTabs.find(t => t.id === serveTabId) || null;
  const serveMl = serveUseCustom
    ? (Number(serveCustomMl) || 0)
    : servePegSize * serveNumPegs;
  const serveAfterMl = selectedServeTab
    ? Math.max(0, selectedServeTab.remaining_ml - serveMl)
    : 0;

  // Fetch the server-computed limits, debounced, as staff type the bill amount.
  // The browser must NOT recompute the rules (see the redeemable comment). One
  // request per keystroke would hammer the counter, so debounce 300ms and abort
  // any in-flight request when a newer keystroke supersedes it. Also runs on the
  // first customer select (bill may be 0). On error/abort we keep the last known
  // value rather than flashing to zero.
  useEffect(() => {
    if (!loyaltyEnabled || !customer?.id) return;
    const ctrl = new AbortController();
    const t = setTimeout(() => {
      api.post(
        "/staff/billing/redemption-limits",
        { customer_id: customer.id, bill_amount: bill },
        { signal: ctrl.signal }
      )
        .then(({ data }) => setLimits(data))
        .catch(() => {}); // aborted or failed → keep last known limits
    }, 300);
    return () => { clearTimeout(t); ctrl.abort(); };
  }, [customer?.id, bill, loyaltyEnabled]);

  // Whenever the ceiling drops (new limits arrived, or the bill shrank), pull the
  // selected points back down so the summary can never show a discount the server
  // will refuse.
  useEffect(() => {
    setPointsToRedeem((p) => (p > redeemable ? redeemable : p));
  }, [redeemable]);

  const submit = async (e) => {
    e.preventDefault();
    if (bill <= 0) { toast.error("Enter a valid bill amount"); return; }
    if (bnCapture === "required" && !billNumber.trim()) {
      toast.error("Bill number is required"); return;
    }
    if (saveBottle && !bottleBrand) {
      toast.error("Pick a bottle brand"); return;
    }
    if (saveBottle && Number(bottleConsumed) > Number(bottleSize)) {
      toast.error("Consumed can't exceed bottle size"); return;
    }
    if (serveFromTab && !serveTabId) {
      toast.error("Select a bottle tab to serve from"); return;
    }
    if (serveFromTab && serveMl <= 0) {
      toast.error("Enter a valid serve amount"); return;
    }
    if (serveFromTab && selectedServeTab && serveMl > selectedServeTab.remaining_ml) {
      toast.error("Serve amount exceeds bottle remaining"); return;
    }
    setLoading(true);
    try {
      const stampAdditionsArray = Object.entries(stampAdditions)
        .filter(([_, c]) => c > 0)
        .map(([program_id, count]) => ({ program_id, count }));
      const { data } = await api.post("/staff/billing/initiate", {
        customer_id: customer.id,
        bill_amount: bill,
        points_to_redeem: loyaltyEnabled ? pointsToRedeem : 0,
        bill_number: bnCapture !== "off" ? billNumber.trim() || null : null,
        stamp_additions: stampAdditionsArray.length > 0 ? stampAdditionsArray : null,
        save_bottle: saveBottle ? {
          brand: bottleBrand,
          bottle_size_ml: Number(bottleSize),
          consumed_today_ml: Number(bottleConsumed) || 0,
          expiry_days: Number(bottleExpiry),
        } : null,
        serve_tab: serveFromTab && serveTabId ? {
          tab_id: serveTabId,
          ...(serveUseCustom
            ? { custom_ml: Number(serveCustomMl) }
            : { peg_size_ml: Number(servePegSize), num_pegs: Number(serveNumPegs) }),
        } : null,
      });
      if (data.requires_otp) {
        setPendingData(data);
        setPhase("otp");
      } else {
        setResult(data);
        setPhase("done");
      }
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setLoading(false);
    }
  };

  if (phase === "otp" && pendingData) {
    return (
      <BillingOTPScreen
        pendingData={pendingData}
        onSuccess={(res) => { setResult(res); setPhase("done"); }}
        onCancel={() => { setPhase("form"); setPendingData(null); }}
      />
    );
  }

  if (phase === "done" && result) {
    return <BillingSuccessScreen result={result} customer={customer} venue={venue} onDone={onClear} />;
  }

  return (
    <form onSubmit={submit} className={`flex flex-col gap-4 ${isMobile ? "pb-[160px]" : ""}`}>
      <div>
        <h3 className="text-lg font-bold mb-0.5">Bill Settlement</h3>
        <p className="text-xs text-[#A0A0A0]">
          Enter the bill. {loyaltyEnabled ? "Points earned on net amount paid." : ""}
        </p>
      </div>

      <div>
        <label className="text-xs font-bold uppercase tracking-wider text-[#A0A0A0] mb-1.5 block">Bill Amount (₹) *</label>
        <input
          required type="number" min="1" step="1"
          data-testid="bill-amount"
          value={billAmount}
          onChange={(e) => {
            const newBill = Number(e.target.value) || 0;
            setBillAmount(e.target.value);
            if (pointsToRedeem > Math.floor(newBill)) {
              setPointsToRedeem(Math.floor(newBill));
            }
          }}
          onWheel={(e) => e.target.blur()}
          className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg px-4 py-3 min-h-[48px] text-white text-xl font-bold focus:border-[#9D00FF] focus:ring-1 focus:ring-[#9D00FF]/40"
          placeholder="₹ 0"
        />
      </div>

      {bnCapture !== "off" && (
        <div>
          <label className="text-xs font-bold uppercase tracking-wider text-[#A0A0A0] mb-1.5 block">
            Bill / Receipt No.{bnCapture === "required" ? " *" : " (optional)"}
          </label>
          <input
            type="text"
            data-testid="bill-number"
            value={billNumber}
            onChange={(e) => setBillNumber(e.target.value)}
            className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg px-4 py-2.5 text-white focus:border-[#9D00FF] focus:ring-1 focus:ring-[#9D00FF]/40"
            placeholder="e.g. INV-1042"
          />
        </div>
      )}

      {loyaltyEnabled && ptsBalance > 0 && bill > 0 && (
        <div className="bg-[#2D1B4E]/40 border border-[#9D00FF]/30 rounded-lg p-4 flex flex-col gap-3">
          <div className="flex items-center justify-between gap-2">
            <span className="text-sm font-semibold text-white">Redeem Points</span>
            {limits && Number(limits.redeemable) < Number(limits.balance) ? (
              <div className="text-right">
                <div className="text-[#9D00FF] font-bold text-sm" data-testid="redeemable-on-bill">
                  🏆 Redeemable on this bill: {redeemable}
                </div>
                <div className="text-[10px] text-[#A0A0A0] mt-0.5">
                  {limits.blocked || `Balance ${limits.balance} pts`}
                </div>
              </div>
            ) : (
              <span className="text-[#9D00FF] font-bold text-sm">🏆 {ptsBalance} pts available</span>
            )}
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-[#A0A0A0] mb-1.5 block">
              Points to redeem:
            </label>
            <input
              type="number"
              min={0}
              max={redeemable}
              step={1}
              value={pointsToRedeem}
              data-testid="redeem-points-input"
              placeholder="0"
              disabled={redeemable <= 0}
              onChange={(e) => {
                const val = Math.max(0, Math.min(redeemable, parseInt(e.target.value, 10) || 0));
                setPointsToRedeem(val);
              }}
              onBlur={() => setPointsToRedeem((p) => Math.max(0, Math.min(redeemable, p)))}
              onWheel={(e) => e.target.blur()}
              className={`w-full bg-[#0A0A0A] border rounded-lg px-4 py-3 min-h-[44px] text-white focus:ring-1 focus:ring-[#9D00FF]/40 disabled:opacity-50 disabled:cursor-not-allowed ${
                pointsToRedeem > redeemable
                  ? "border-red-500 focus:border-red-500"
                  : "border-white/10 focus:border-[#9D00FF]"
              }`}
            />
            {pointsToRedeem > 0 && pointsToRedeem <= redeemable && (
              <p className="text-xs text-emerald-400 font-semibold mt-1">= ₹{pointsToRedeem} off</p>
            )}
            {redeemable <= 0 && (
              <p className="text-xs text-amber-400 font-semibold mt-1" data-testid="redeem-blocked-msg">
                {limits?.blocked || "No points can be redeemed on this bill"}
              </p>
            )}
          </div>
          <div className="flex gap-2 flex-wrap">
            <button type="button" onClick={() => setPointsToRedeem(0)}
              className={`px-3 py-1.5 rounded-md text-xs font-semibold border transition ${pointsToRedeem === 0 ? "bg-[#9D00FF] border-[#9D00FF] text-white" : "bg-[#0A0A0A] border-white/20 text-[#A0A0A0] hover:text-white"}`}>
              None
            </button>
            <button type="button" disabled={redeemable <= 0} onClick={() => setPointsToRedeem(Math.floor(redeemable / 2))}
              className={`px-3 py-1.5 rounded-md text-xs font-semibold border transition disabled:opacity-40 disabled:cursor-not-allowed ${
                pointsToRedeem === Math.floor(redeemable / 2) && pointsToRedeem !== 0
                  ? "bg-[#9D00FF] border-[#9D00FF] text-white"
                  : "bg-[#0A0A0A] border-white/20 text-[#A0A0A0] hover:text-white"
              }`}>
              Half
            </button>
            <button type="button" disabled={redeemable <= 0} onClick={() => setPointsToRedeem(redeemable)}
              className={`px-3 py-1.5 rounded-md text-xs font-semibold border transition disabled:opacity-40 disabled:cursor-not-allowed ${
                pointsToRedeem === redeemable && pointsToRedeem !== 0
                  ? "bg-[#9D00FF] border-[#9D00FF] text-white"
                  : "bg-[#0A0A0A] border-white/20 text-[#A0A0A0] hover:text-white"
              }`}>
              Max {redeemable} pts
            </button>
          </div>
        </div>
      )}

      {hasFeature("stamp_cards") && programs.length > 0 && (
        <div className="mb-4 bg-[#1A1A1A] border border-white/10 rounded-lg p-4 flex flex-col gap-3">
          <span className="text-sm font-semibold text-white">Add Stamps</span>
          {programs.map((p) => (
            <div key={p.id} className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="text-sm text-white font-medium truncate">{p.name}</div>
                <div className="text-xs text-[#A0A0A0]">{p.reward_title}</div>
              </div>
              <div className="flex items-center bg-[#0A0A0A] border border-white/10 rounded-lg overflow-hidden shrink-0">
                <button
                  type="button"
                  onClick={() => setStampAdditions(prev => ({ ...prev, [p.id]: Math.max(0, (prev[p.id] || 0) - 1) }))}
                  className="px-3 py-2 text-lg font-bold text-[#9D00FF] hover:bg-white/5"
                >
                  −
                </button>
                <div className="px-3 text-sm font-bold text-white min-w-[2rem] text-center">
                  {stampAdditions[p.id] || 0}
                </div>
                <button
                  type="button"
                  onClick={() => setStampAdditions(prev => ({ ...prev, [p.id]: (prev[p.id] || 0) + 1 }))}
                  className="px-3 py-2 text-lg font-bold text-[#9D00FF] hover:bg-white/5"
                >
                  +
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Bottle save section */}
      {bottleLockerEnabled && (
        <div className="bg-[#1A1A1A] border border-white/10 rounded-lg p-4 flex flex-col gap-3">
          {/* Toggle row */}
          <label className="flex items-center justify-between cursor-pointer">
            <span className="text-sm font-semibold text-white">Save a bottle?</span>
            <input
              type="checkbox"
              checked={saveBottle}
              onChange={(e) => setSaveBottle(e.target.checked)}
              className="accent-[#9D00FF] w-4 h-4"
            />
          </label>

          {saveBottle && (
            <>
              {/* Brand search */}
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-[#A0A0A0] mb-1.5 block">Brand</label>
                <div className="relative">
                  <input
                    data-testid="bottle-brand-search-input"
                    value={bottleBrandQuery}
                    onChange={(e) => {
                      setBrandQuery(e.target.value);
                      setBottleBrand("");
                    }}
                    placeholder="Search or pick a brand…"
                    className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg px-4 py-3 min-h-[44px] text-white focus:border-[#9D00FF] focus:ring-1 focus:ring-[#9D00FF]/40"
                  />
                  {bottleBrandQuery && !bottleBrand && (
                    <div className="absolute z-10 left-0 right-0 top-full mt-1 bg-[#1A1A1A] border border-white/10 rounded-lg max-h-56 overflow-y-auto shadow-xl">
                      {bottleBrands
                        .filter((b) => b.toLowerCase().includes(bottleBrandQuery.toLowerCase()))
                        .slice(0, 8)
                        .map((b) => (
                          <button
                            type="button"
                            key={b}
                            onClick={() => {
                              setBottleBrand(b);
                              setBrandQuery(b);
                            }}
                            className="w-full text-left px-4 py-2.5 hover:bg-[#9D00FF]/10 text-white text-sm border-b border-white/5 last:border-0"
                          >
                            {b}
                          </button>
                        ))}
                      <button
                        type="button"
                        onClick={() => setShowAddBrand(true)}
                        className="w-full text-left px-4 py-2.5 text-[#9D00FF] font-semibold text-sm hover:bg-[#9D00FF]/10"
                      >
                        + Add new brand
                      </button>
                    </div>
                  )}
                </div>
                {bottleBrand && (
                  <div className="mt-2 text-xs text-[#9D00FF]">
                    Selected: <span className="text-white font-semibold">{bottleBrand}</span>
                  </div>
                )}
              </div>

              {showAddBrand && (
                <div className="bg-[#0A0A0A] border border-[#9D00FF]/30 rounded-lg p-3 flex gap-2">
                  <input
                    value={newBrandName}
                    onChange={(e) => setNewBrandName(e.target.value)}
                    placeholder="Custom brand name"
                    className="flex-1 bg-transparent text-white outline-none px-2"
                  />
                  <button
                    type="button"
                    onClick={async () => {
                      if (!newBrandName.trim()) return;
                      try {
                        await api.post("/brands", { name: newBrandName.trim() });
                        const { data } = await api.get("/brands");
                        setBottleBrands(data.brands || []);
                        setBottleBrand(newBrandName.trim());
                        setBrandQuery(newBrandName.trim());
                        setNewBrandName("");
                        setShowAddBrand(false);
                        toast.success("Brand added");
                      } catch (err) { toast.error(formatError(err)); }
                    }}
                    className="bg-[#9D00FF] px-3 py-1.5 rounded text-sm font-semibold"
                  >
                    Save
                  </button>
                  <button type="button" onClick={() => setShowAddBrand(false)} className="text-[#A0A0A0] px-2 text-sm">
                    Cancel
                  </button>
                </div>
              )}

              {/* Bottle size */}
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-[#A0A0A0] mb-2 block">Bottle Size</label>
                <div className="grid grid-cols-3 gap-2">
                  {[{ v: 750, label: "750 ml" }, { v: 1000, label: "1 L" }, { v: 1500, label: "1.5 L" }].map((opt) => (
                    <button
                      type="button"
                      key={opt.v}
                      onClick={() => setBottleSize(opt.v)}
                      className={`py-3 min-h-[44px] rounded-lg font-semibold text-sm border transition ${
                        bottleSize === opt.v
                          ? "bg-[#9D00FF] border-[#9D00FF] text-white"
                          : "bg-[#0A0A0A] border-white/10 text-[#A0A0A0] hover:text-white"
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Consumed input */}
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-[#A0A0A0] mb-1.5 block">Consumed tonight (ml)</label>
                <input
                  type="number"
                  min="0"
                  max={bottleSize}
                  value={bottleConsumed}
                  onChange={(e) => setBottleConsumed(e.target.value)}
                  onWheel={(e) => e.target.blur()}
                  className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg px-4 py-3 min-h-[44px] text-white focus:border-[#9D00FF] focus:ring-1 focus:ring-[#9D00FF]/40"
                />
              </div>

              {/* Expiry */}
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-[#A0A0A0] mb-2 block">Expiry</label>
                <div className="grid grid-cols-3 gap-2">
                  {[30, 60, 90].map((d) => (
                    <button
                      type="button"
                      key={d}
                      onClick={() => setBottleExpiry(d)}
                      className={`py-3 min-h-[44px] rounded-lg font-semibold text-sm border transition ${
                        bottleExpiry === d
                          ? "bg-[#9D00FF] border-[#9D00FF] text-white"
                          : "bg-[#0A0A0A] border-white/10 text-[#A0A0A0] hover:text-white"
                      }`}
                    >
                      {d} days
                    </button>
                  ))}
                </div>
              </div>

              {/* Remaining display */}
              <p className="text-xs text-[#A0A0A0]">
                Remaining: {bottleSize - (Number(bottleConsumed) || 0)} ml
              </p>
            </>
          )}
        </div>
      )}

      {/* Serve from existing tab */}
      {bottleLockerEnabled && !saveBottle && activeTabs.length > 0 && (
        <div className="border border-white/10 rounded-xl p-4 flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <div>
              <div className="font-semibold text-sm">Serve from existing tab?</div>
              <div className="text-xs text-[#A0A0A0]">Deduct from customer's stored bottle</div>
            </div>
            <button
              type="button"
              onClick={() => { setServeFromTab(v => !v); setServeTabId(""); setServeNumPegs(1); setServeCustomMl(""); setServeUseCustom(false); }}
              className={`relative w-10 h-6 rounded-full transition-colors ${serveFromTab ? "bg-[#9D00FF]" : "bg-white/10"}`}
            >
              <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${serveFromTab ? "translate-x-4" : ""}`} />
            </button>
          </div>

          {serveFromTab && (
            <>
              <div className="flex flex-col gap-2">
                <label className="text-xs font-bold uppercase tracking-wider text-[#A0A0A0]">Select bottle</label>
                <div className="flex flex-col gap-1.5">
                  {activeTabs.map(t => (
                    <button key={t.id} type="button" onClick={() => setServeTabId(t.id)}
                      className={`flex items-center justify-between px-3 py-2 rounded-lg border text-sm transition ${serveTabId === t.id ? "border-[#9D00FF] bg-[#2D1B4E]/30 text-white" : "border-white/10 bg-[#0A0A0A] text-[#A0A0A0] hover:text-white"}`}
                    >
                      <span>{t.brand} ({t.bottle_size_ml}ml)</span>
                      <span>{t.remaining_ml}ml left</span>
                    </button>
                  ))}
                </div>
              </div>

              {serveTabId && (
                <>
                  <div className="flex items-center gap-2">
                    <button type="button" onClick={() => setServeUseCustom(false)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition ${!serveUseCustom ? "bg-[#9D00FF] border-[#9D00FF] text-white" : "bg-[#0A0A0A] border-white/10 text-[#A0A0A0]"}`}
                    >By pegs</button>
                    <button type="button" onClick={() => setServeUseCustom(true)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition ${serveUseCustom ? "bg-[#9D00FF] border-[#9D00FF] text-white" : "bg-[#0A0A0A] border-white/10 text-[#A0A0A0]"}`}
                    >Custom ml</button>
                  </div>

                  {!serveUseCustom ? (
                    <div className="flex flex-col gap-2">
                      <label className="text-xs font-bold uppercase tracking-wider text-[#A0A0A0]">Peg size</label>
                      <div className="flex flex-wrap gap-2">
                        {[30, 60, 90].map(ml => (
                          <button key={ml} type="button" onClick={() => setServePegSize(ml)}
                            className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition ${servePegSize === ml ? "bg-[#9D00FF] border-[#9D00FF] text-white" : "bg-[#0A0A0A] border-white/10 text-[#A0A0A0]"}`}
                          >{ml}ml</button>
                        ))}
                      </div>
                      <label className="text-xs font-bold uppercase tracking-wider text-[#A0A0A0] mt-1">Number of pegs</label>
                      <div className="flex items-center gap-3">
                        <button type="button" onClick={() => setServeNumPegs(p => Math.max(1, p - 1))}
                          className="w-9 h-9 rounded-full border border-white/20 flex items-center justify-center text-white text-lg font-bold hover:bg-white/10">−</button>
                        <span className="text-white font-bold text-lg w-8 text-center">{serveNumPegs}</span>
                        <button type="button" onClick={() => setServeNumPegs(p => p + 1)}
                          className="w-9 h-9 rounded-full border border-white/20 flex items-center justify-center text-white text-lg font-bold hover:bg-white/10">+</button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-col gap-2">
                      <label className="text-xs font-bold uppercase tracking-wider text-[#A0A0A0]">Amount to serve (ml)</label>
                      <input type="number" min="1" value={serveCustomMl} onChange={e => setServeCustomMl(e.target.value)}
                        className="bg-[#0A0A0A] border border-white/20 rounded-lg px-3 py-2 text-white text-sm focus:border-[#9D00FF] outline-none w-32" />
                    </div>
                  )}

                  {serveMl > 0 && (
                    <p className="text-xs text-[#A0A0A0]">
                      Serving {serveMl}ml → {serveAfterMl}ml remaining
                    </p>
                  )}
                </>
              )}
            </>
          )}
        </div>
      )}

      {/* Bill summary + confirm — inline on desktop, fixed footer on mobile */}
      {isMobile ? (
        <div className="fixed bottom-[64px] left-0 right-0 z-20 bg-[#1A1A1A] border-t border-white/10 px-4 py-3 flex flex-col gap-2">
          {bill > 0 && (
            <div className="flex items-center justify-between gap-3 text-sm">
              <div className="flex items-center gap-3">
                <span className="text-[#A0A0A0]">Bill <span className="text-white font-bold">₹{bill}</span></span>
                {loyaltyEnabled && pointsToRedeem > 0 && (
                  <span className="text-[#9D00FF] font-bold">− ₹{pointsToRedeem}</span>
                )}
              </div>
              <span className="text-white font-black text-base">₹{netPayable}</span>
            </div>
          )}
          {bill > 0 && loyaltyEnabled && willEarn > 0 && (
            <div className="text-xs text-[#9D00FF] font-semibold">🏆 Will earn {willEarn} pts</div>
          )}
          <button
            type="submit"
            disabled={loading || bill <= 0}
            data-testid="bill-settle-submit"
            className="tb-glow bg-[#9D00FF] hover:bg-[#8C00E6] disabled:opacity-50 text-white font-semibold rounded-lg py-3 min-h-[48px] transition-all w-full"
          >
            {loading ? "Processing…" : pointsToRedeem > 0 ? "Send Redemption OTP" : (saveBottle || serveFromTab) ? "Send OTP to Confirm" : "Confirm & Award Points"}
          </button>
        </div>
      ) : (
        <>
          {bill > 0 && (
            <div className="bg-[#0A0A0A] border border-white/10 rounded-lg p-4 grid grid-cols-3 gap-3 text-center">
              <div>
                <div className="text-xs text-[#A0A0A0] mb-1">Bill</div>
                <div className="text-white font-bold">₹{bill}</div>
              </div>
              {loyaltyEnabled && (
                <div>
                  <div className="text-xs text-[#A0A0A0] mb-1">Points Off</div>
                  <div className="text-[#9D00FF] font-bold">- ₹{pointsToRedeem}</div>
                </div>
              )}
              <div>
                <div className="text-xs text-[#A0A0A0] mb-1">Net Payable</div>
                <div className="text-white font-bold text-lg">₹{netPayable}</div>
              </div>
              {loyaltyEnabled && willEarn > 0 && (
                <div className="col-span-3 text-xs text-[#9D00FF] font-semibold pt-1 border-t border-white/5">
                  🏆 Will earn {willEarn} points on this visit
                </div>
              )}
            </div>
          )}
          <button
            type="submit"
            disabled={loading || bill <= 0}
            data-testid="bill-settle-submit"
            className="tb-glow bg-[#9D00FF] hover:bg-[#8C00E6] disabled:opacity-50 text-white font-semibold rounded-lg py-3.5 min-h-[48px] transition-all"
          >
            {loading ? "Processing…" : pointsToRedeem > 0 ? "Send Redemption OTP" : (saveBottle || serveFromTab) ? "Send OTP to Confirm" : "Confirm & Award Points"}
          </button>
        </>
      )}
    </form>
  );
}

function BillingOTPScreen({ pendingData, onSuccess, onCancel }) {
  const [digits, setDigits] = useState(Array(6).fill(""));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [shake, setShake] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(30);
  const [showOverride, setShowOverride] = useState(false);
  const inputRefs = useRef([]);

  useEffect(() => { inputRefs.current[0]?.focus(); }, []);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown(c => Math.max(0, c - 1)), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  const submitOTP = async (code) => {
    if (loading) return;
    setLoading(true); setError("");
    try {
      const { data } = await api.post("/staff/billing/verify-otp", { pending_id: pendingData.pending_id, otp_code: code });
      onSuccess(data);
    } catch (err) {
      setError(formatError(err)); setShake(true);
      setTimeout(() => setShake(false), 600);
      setDigits(Array(6).fill(""));
      setTimeout(() => inputRefs.current[0]?.focus(), 50);
    } finally { setLoading(false); }
  };

  const handleDigitChange = (idx, val) => {
    const d = val.replace(/\D/g, "").slice(-1);
    const next = [...digits]; next[idx] = d; setDigits(next);
    if (d && idx < 5) inputRefs.current[idx + 1]?.focus();
    if (next.every(x => x !== "") && d) submitOTP(next.join(""));
  };

  const handleKeyDown = (idx, e) => {
    if (e.key === "Backspace" && !digits[idx] && idx > 0) {
      const next = [...digits]; next[idx - 1] = ""; setDigits(next);
      inputRefs.current[idx - 1]?.focus();
    }
    if (e.key === "Enter") { const code = digits.join(""); if (code.length === 6) submitOTP(code); }
  };

  const handleResend = async () => {
    if (resendCooldown > 0) return;
    try {
      await api.post("/staff/billing/resend-otp", { pending_id: pendingData.pending_id });
      setResendCooldown(30); setDigits(Array(6).fill("")); setError("");
      setTimeout(() => inputRefs.current[0]?.focus(), 50);
      toast.success("New OTP sent");
    } catch (err) { toast.error(formatError(err)); }
  };

  return (
    <div className="flex flex-col gap-5">
      <div className="text-center">
        <div className="text-3xl mb-2">🔐</div>
        <h3 className="text-lg font-bold">Redemption OTP</h3>
        <p className="text-sm text-[#A0A0A0] mt-1">
          Sent to <span className="text-white font-medium">{pendingData.customer_phone_masked}</span>
        </p>
      </div>
      <div className="bg-[#0A0A0A] border border-white/10 rounded-lg p-4 grid grid-cols-3 gap-3 text-center">
        <div><div className="text-xs text-[#A0A0A0] mb-1">Bill</div><div className="font-bold text-white">₹{pendingData.bill_amount}</div></div>
        <div><div className="text-xs text-[#A0A0A0] mb-1">Points Off</div><div className="font-bold text-[#9D00FF]">-₹{pendingData.points_to_redeem}</div></div>
        <div><div className="text-xs text-[#A0A0A0] mb-1">Net Payable</div><div className="font-bold text-white text-lg">₹{pendingData.net_payable}</div></div>
      </div>
      {pendingData.serve_tab_summary && (
        <div className="bg-[#0A0A0A] border border-white/10 rounded-lg px-4 py-3 text-sm text-[#A0A0A0] text-center">
          🥃 {pendingData.serve_tab_summary}
        </div>
      )}
      <div className={`flex justify-center gap-2 ${shake ? "animate-pulse" : ""}`}>
        {digits.map((d, i) => (
          <input key={i} ref={el => inputRefs.current[i] = el} type="text" inputMode="numeric" maxLength={1} value={d}
            onChange={e => handleDigitChange(i, e.target.value)} onKeyDown={e => handleKeyDown(i, e)} disabled={loading}
            className={`w-12 h-14 text-center text-2xl font-bold rounded-xl border-2 bg-[#0A0A0A] text-white transition-all outline-none ${d ? "border-[#9D00FF] bg-[#2D1B4E]/40" : "border-white/20"} ${shake ? "border-red-500" : ""} focus:border-[#9D00FF]`}
          />
        ))}
      </div>
      {error && <div className="text-center text-red-400 text-sm">{error}</div>}
      {loading && <div className="text-center text-[#9D00FF] text-sm">Verifying…</div>}
      <button type="button" onClick={handleResend} disabled={resendCooldown > 0}
        className="w-full flex items-center justify-center gap-2 py-3 rounded-lg border font-semibold text-sm disabled:opacity-40 bg-[#0A0A0A] border-white/20 text-white hover:border-[#9D00FF]/60 transition">
        <RotateCcw size={14} />
        {resendCooldown > 0 ? `Resend OTP (${resendCooldown}s)` : "Resend OTP"}
      </button>
      <div className="text-center">
        <button type="button" onClick={() => setShowOverride(true)} className="text-xs text-[#A0A0A0] hover:text-white underline">Customer can't receive OTP?</button>
      </div>
      <button type="button" onClick={onCancel} className="text-center text-xs text-[#A0A0A0] hover:text-white">← Back to form</button>
      {showOverride && (
        <ManagerOverrideModal pendingId={pendingData.pending_id} apiBase="/staff/billing" onSuccess={onSuccess} onClose={() => setShowOverride(false)} />
      )}
    </div>
  );
}

function BillingSuccessScreen({ result, customer, venue, onDone }) {
  const splitEnabled = venue?.allow_points_split && result.points_earned > 0;
  const [numPeople, setNumPeople] = useState("");
  const [splitStatus, setSplitStatus] = useState(null); // null | "sent" | "no_points"
  const [splitLoading, setSplitLoading] = useState(false);

  useEffect(() => {
    if (!splitEnabled) {
      const t = setTimeout(onDone, 3000);
      return () => clearTimeout(t);
    }
  }, [splitEnabled, onDone]);

  const handleSplit = async () => {
    const n = parseInt(numPeople, 10);
    if (!n || n < 2) return;
    setSplitLoading(true);
    try {
      const { data } = await api.post("/staff/split/points", {
        customer_id: customer.id,
        bill_id: "",
        total_points: result.points_earned,
        num_people: n,
      });
      if (data.split === false) {
        setSplitStatus("no_points");
      } else {
        setSplitStatus("sent");
      }
    } catch {
      setSplitStatus("no_points");
    } finally {
      setSplitLoading(false);
    }
  };

  return (
    <div className="flex flex-col items-center gap-4 py-10 text-center">
      <div className="w-16 h-16 rounded-full bg-emerald-500/20 border border-emerald-500/40 flex items-center justify-center">
        <Check size={28} className="text-emerald-400" />
      </div>
      <div className="w-full max-w-xs">
        <div className="text-lg font-bold text-white">Bill Settled! 🎉</div>
        <p className="text-sm text-[#A0A0A0] mt-1">{customer.name}</p>
        <div className="mt-3 grid grid-cols-3 gap-3 text-center bg-[#0A0A0A] border border-white/10 rounded-lg p-4">
          <div><div className="text-xs text-[#A0A0A0]">Bill</div><div className="font-bold text-white">₹{result.bill_amount}</div></div>
          <div><div className="text-xs text-[#A0A0A0]">Net Paid</div><div className="font-bold text-white">₹{result.net_payable}</div></div>
          <div><div className="text-xs text-[#A0A0A0]">Points Earned</div><div className="font-bold text-[#9D00FF]">+{result.points_earned}</div></div>
        </div>

        {splitEnabled && !splitStatus && (
          <div className="mt-4 bg-[#1A1A1A] border border-white/10 rounded-lg p-4 text-left">
            <div className="text-sm font-semibold text-white mb-0.5">Split rewards with the table?</div>
            <div className="text-xs text-[#A0A0A0] mb-3">Enter total people including yourself</div>
            <div className="flex gap-2">
              <input
                type="number"
                min={2}
                value={numPeople}
                onChange={(e) => setNumPeople(e.target.value)}
                onWheel={(e) => e.target.blur()}
                placeholder="e.g. 4"
                className="flex-1 bg-[#0A0A0A] border border-white/10 rounded-lg px-3 py-2 text-sm text-white"
              />
              <button
                type="button"
                onClick={handleSplit}
                disabled={splitLoading}
                className="bg-[#9D00FF] hover:bg-[#8C00E6] disabled:opacity-50 text-white text-xs font-bold rounded-lg px-3 py-2 whitespace-nowrap"
              >
                {splitLoading ? "…" : "Generate Split Link"}
              </button>
            </div>
          </div>
        )}

        {splitStatus === "sent" && (
          <div className="mt-4 text-sm text-emerald-400">
            Split link sent to {customer.name} via WhatsApp ✓
          </div>
        )}
        {splitStatus === "no_points" && (
          <div className="mt-4 text-xs text-[#A0A0A0]">Not enough points to split</div>
        )}

        {splitEnabled && (
          <button type="button" onClick={onDone} className="mt-4 text-xs text-[#A0A0A0] hover:text-white">
            Done
          </button>
        )}
      </div>
    </div>
  );
}

// ---------------- Add Stamps ----------------
function AddStampsForm({ customer, onDone, onClear }) {
  const [programs, setPrograms] = useState([]);
  const [selectedProgramId, setSelectedProgramId] = useState("");
  const [count, setCount] = useState(1);
  const [loading, setLoading] = useState(false);
  const [phase, setPhase] = useState("form"); // "form" | "done"

  useEffect(() => {
    api.get("/stamp-programs").then(({ data }) => {
      const active = (data || []).filter((p) => p.active);
      setPrograms(active);
      if (active.length > 0) setSelectedProgramId(active[0].id);
    }).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!selectedProgramId) { toast.error("Select a stamp program"); return; }
    setLoading(true);
    try {
      await api.post("/stamps", { customer_id: customer.id, program_id: selectedProgramId, count });
      setPhase("done");
      setTimeout(onClear, 3000);
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setLoading(false);
    }
  };

  if (phase === "done") {
    return (
      <div className="flex flex-col items-center justify-center gap-4 py-10 text-center">
        <div className="w-16 h-16 rounded-full bg-emerald-500/20 border border-emerald-500/40 flex items-center justify-center">
          <Check size={28} className="text-emerald-400" />
        </div>
        <div>
          <div className="text-lg font-bold text-white">Stamps Added!</div>
          <p className="text-sm text-[#A0A0A0] mt-1">
            {count} stamp{count !== 1 ? "s" : ""} added for {customer.name}
          </p>
        </div>
      </div>
    );
  }

  if (programs.length === 0) {
    return (
      <div className="text-center py-10">
        <ClipboardList size={32} className="mx-auto text-[#9D00FF] mb-2" />
        <div className="font-semibold">No active stamp programs</div>
        <p className="text-sm text-[#A0A0A0] mt-1">Create a stamp program in Settings first.</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="flex flex-col gap-5">
      <div>
        <h3 className="text-lg font-bold mb-0.5">Add Stamps</h3>
        <p className="text-xs text-[#A0A0A0]">Add stamps to a customer's loyalty card.</p>
      </div>

      <div>
        <label className="text-xs font-bold uppercase tracking-wider text-[#A0A0A0] mb-2 block">Stamp Program</label>
        <div className="flex flex-col gap-2">
          {programs.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => setSelectedProgramId(p.id)}
              className={`text-left p-4 rounded-lg border transition ${
                selectedProgramId === p.id
                  ? "bg-[#9D00FF]/15 border-[#9D00FF]"
                  : "bg-[#0A0A0A] border-white/10 hover:border-[#9D00FF]/40"
              }`}
            >
              <div className="font-semibold text-white">{p.name}</div>
              <div className="text-xs text-[#A0A0A0] mt-0.5">
                Buy {p.required_stamps} → {p.reward_title}
              </div>
            </button>
          ))}
        </div>
      </div>

      <div>
        <label className="text-xs font-bold uppercase tracking-wider text-[#A0A0A0] mb-2 block">Stamps to add</label>
        <div className="flex items-center bg-[#0A0A0A] border border-white/10 rounded-lg overflow-hidden">
          <button
            type="button"
            onClick={() => setCount(Math.max(1, count - 1))}
            className="px-4 py-3 min-h-[44px] text-xl font-bold text-[#9D00FF] hover:bg-white/5"
          >
            −
          </button>
          <div className="flex-1 text-center text-xl font-bold text-white">{count}</div>
          <button
            type="button"
            onClick={() => setCount(count + 1)}
            className="px-4 py-3 min-h-[44px] text-xl font-bold text-[#9D00FF] hover:bg-white/5"
          >
            +
          </button>
        </div>
      </div>

      <button
        type="submit"
        disabled={loading || !selectedProgramId}
        data-testid="add-stamps-submit"
        className="tb-glow bg-[#9D00FF] hover:bg-[#8C00E6] disabled:opacity-50 text-white font-semibold rounded-lg py-3.5 min-h-[48px] transition-all"
      >
        {loading ? "Adding…" : `Add ${count} Stamp${count !== 1 ? "s" : ""}`}
      </button>
    </form>
  );
}
